import React, { ChangeEvent, FC, useRef } from 'react';
import { Grid, Box, Typography, Checkbox } from '@mui/material';
import { isEmpty } from 'lodash';
import { useBoundStore } from 'store';
import { useDocumentPrintContext } from 'store/DocumentPrint';
import { usePartialPublish, handleSectionClick, useSectionsToPublish } from 'store/DocumentPublish';
import { Article } from 'modules/Document/Article/Article';
import { getHeaderVariant } from 'modules/Document/Article/helpers';
import { BlockWrapper as Block } from 'modules/Document/Block/Block';
import { getAddOrDeleteEntityColorForHistory, getModifiedEntityColorForHistory } from 'modules/Document/helpers';
import FileButton from 'shared/components/form/FileButton/FileButton';
import { EditableContent } from 'shared/components/form/EditableContent/EditableContent';

export const Section: FC<any> = ({ section, siblings, showComments = true }) => {
  const ref = useRef<HTMLElement>();
  const partialPublish = usePartialPublish();
  const { isPrintMode, isPrintPreview, itemsToPrint, toggleItemToPrint } = useDocumentPrintContext();
  const sectionsToPublish = useSectionsToPublish();
  const showDraftVersion = useBoundStore((state) => state.showDraftVersion);

  const newOrDeletedColor = getAddOrDeleteEntityColorForHistory(section.id, 'section');
  const modifiedColorTitle = getModifiedEntityColorForHistory(section.id, 'section', 'title');
  const modifiedColorText = getModifiedEntityColorForHistory(section.id, 'section', 'text_content');

  const onPublishCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const currentDocument = document || { section_set: siblings };
    handleSectionClick(section.id, checked, true, currentDocument);
  };

  const isSectionSelected = sectionsToPublish.includes(section.id);

  return (
    <Box
      ref={ref}
      className="document-child-item"
      id={section.slug}
      sx={{
        background: newOrDeletedColor,
        border: modifiedColorText && !modifiedColorTitle ? `4px dashed ${modifiedColorText}` : undefined,
      }}
    >
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography
            variant="h3"
            id={section.slug}
            sx={{
              wordBreak: 'break-word',
              color: section.titles_color,
              background: modifiedColorTitle,
              border: modifiedColorTitle && modifiedColorText ? `4px dashed ${modifiedColorText}` : undefined,
            }}
          >
            {(!showDraftVersion && partialPublish) && (
              <Checkbox
                checked={isSectionSelected}
                sx={{ ml: -3, mt: -0.5 }}
                onChange={onPublishCheckboxChange}
              />
            )}
            {(isPrintMode && !isPrintPreview) && (
              <span data-slug={section.slug}>
                <Checkbox
                  key={`${itemsToPrint[section.slug]}`}
                  defaultChecked={itemsToPrint[section.slug]}
                  size="small"
                  className="print-selection-checkbox"
                  sx={{ ml: -2, mt: -0.5 }}
                  onChange={event => toggleItemToPrint(section.slug, event.target.checked)}
                />
              </span>
            )}
            <EditableContent section={section} siblings={siblings} disableEnterKey>
              {section.stripped_title}
              {showComments && <FileButton section={section}/>}
            </EditableContent>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {!isEmpty(section.blocks) && section.blocks.map((block: any) => (
            (block.is_before_article && block.hasContent) && (
              <Box key={block.slug}>
                <Block
                  parent={section}
                  block={block}
                  titleVariant={getHeaderVariant(1)}
                  siblings={section.blocks}
                  allowPrintSelection
                  showComments={showComments}
                />
              </Box>
            )
          ))}

          {!isEmpty(section.article_set) && section.article_set.map((article: any, index: number) => (
            article.title && (
              <Article
                parent={section}
                siblings={section.article_set}
                article={article}
                key={article.slug}
                level={1}
                position={index}
                showComments={showComments}
              />
            )
          ))}

          {!isEmpty(section.blocks) && section.blocks.map((block: any) => (
            (!block.is_before_article && block.hasContent) && (
              <Box key={block.slug}>
                <Block
                  block={block}
                  titleVariant={getHeaderVariant(1)}
                  siblings={section.blocks}
                  parent={section}
                  allowPrintSelection
                  showComments={showComments}
                />
              </Box>
            )
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};
